import { createContext, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { authFetch } from "@/lib/auth-fetch";
import { apiUrl } from "@/lib/api-base";
import { getSession as getStoredSession, clearSession, apiLogout, type RawAuthUser } from "@/lib/auth-session";
import {
  getLoginUrl,
  getMarketingUrl,
  isCrossDomainConfigured,
  isOnAppDomain,
} from "@/lib/auth-redirect";
import { clearAuthFlag, setAuthFlag } from "@/lib/auth-flag";
import { storeAuthError } from "@/lib/auth-messages";

export interface AuthUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  displayName: string | null;
  journalingGoal: string | null;
  onboardingCompleted: boolean;
  /**
   * Plan identifier from API. `free_trial` is the default for new accounts;
   * `plus` / `premium` are paid Stripe plans; `subscriber` / `unlimited_trial`
   * are legacy "anything paid / comped" values kept for compatibility.
   * Omitted on older backends until migration.
   */
  plan?: "free_trial" | "plus" | "premium" | "subscriber" | "unlimited_trial";
  trialEntriesUsed?: number;
  /**
   * Entry allowance: 5 total on the free trial, 50/month on Plus, 120/month
   * on Premium. `null` only for legacy unlimited plans (subscriber, unlimited_trial).
   */
  trialEntriesLimit?: number | null;
  /** ISO timestamp — when the current paid period ends and Stripe will renew. */
  planRenewsAt?: string | null;
  /** ISO timestamp — set while the subscription is scheduled to cancel; equal to planRenewsAt. */
  planCancelsAt?: string | null;
  /** Plan tier ("plus" | "premium") queued to take over at `pendingPlanEffectiveAt`, if a switch is pending. */
  pendingPlan?: "plus" | "premium" | null;
  /** ISO timestamp `pendingPlan` takes effect — equal to planRenewsAt at the time it was scheduled. */
  pendingPlanEffectiveAt?: string | null;
  /** Which Stripe account bills the current plan (null: no subscription, e.g. Starter or a comp grant). */
  billingAccount?: "legacy" | "v2" | null;
  /** Stripe status of that subscription — `past_due` while a failed renewal is being retried. */
  subscriptionStatus?: string | null;
}

export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  /** Re-sync profile from `/auth/user`. Pass `{ silent: true }` to avoid the global loading screen. */
  refetch: (options?: { silent?: boolean }) => void;
  /** Sets the user directly from a login/signup response, skipping a redundant /auth/user round trip. */
  hydrateUser: (user: RawAuthUser) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// These pages own their own auth lifecycle (login.tsx clears any stale local
// session on mount; reset-password.tsx/auth-callback.tsx are mid-exchange) — the
// background /auth/user sync has nothing useful to do there and only risks acting
// on a session that's about to be replaced or torn down anyway.
const AUTH_FLOW_PATHS = new Set(["/login", "/auth/callback", "/reset-password"]);
function isOnAuthFlowPath(): boolean {
  return typeof window !== "undefined" && AUTH_FLOW_PATHS.has(window.location.pathname);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const cancelledRef = useRef(false);
  /** Only the first hydration pass should block the app behind the global spinner. */
  const initialSyncPendingRef = useRef(true);

  const syncFromServer = useCallback(async (silent = false) => {
    const showGlobalLoader = !silent && initialSyncPendingRef.current;
    if (showGlobalLoader) setIsLoading(true);

    const finishSync = () => {
      if (cancelledRef.current) return;
      if (showGlobalLoader) setIsLoading(false);
      initialSyncPendingRef.current = false;
    };

    if (isOnAuthFlowPath()) {
      if (!cancelledRef.current) setUser(null);
      finishSync();
      return;
    }

    const stored = getStoredSession();
    if (!stored) {
      // The app domain is the source of truth for "is there a session?".
      // If we're here on app.* with no session, clear the cross-subdomain
      // flag so the marketing bouncer doesn't loop us back.
      if (isOnAppDomain()) clearAuthFlag();
      if (!cancelledRef.current) setUser(null);
      finishSync();
      return;
    }

    // We have a local session — make sure the cross-subdomain flag is set
    // so a tab on unfoldwin.com knows to bounce over to the app.
    setAuthFlag();

    try {
      const res = await authFetch(apiUrl("/auth/user"));
      if (res.status === 401) {
        // authFetch already tried a silent refresh-and-retry internally; a 401
        // surviving that means the session is genuinely dead (revoked, or the
        // refresh token itself expired) — tell the user plainly rather than
        // silently dropping them to a logged-out state with no explanation.
        console.error("[auth] /auth/user rejected the current session (401)");
        storeAuthError("Your session has expired. Please sign in again.");
        clearSession();
        throw new Error("HTTP 401");
      }
      if (!res.ok || res.status === 304) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as { user: AuthUser | null };
      if (!data.user) {
        throw new Error("API returned no user profile");
      }
      if (!cancelledRef.current) setUser(data.user);
    } catch {
      if (!cancelledRef.current) setUser(null);
    } finally {
      finishSync();
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    initialSyncPendingRef.current = true;
    void syncFromServer(false);

    // A background refresh triggered by some other API call (outside this effect,
    // e.g. from a page's own data fetch) can fail independently of syncFromServer —
    // this keeps context state consistent with that outcome either way.
    function onSessionCleared() {
      if (cancelledRef.current) return;
      clearAuthFlag();
      setUser(null);
    }
    window.addEventListener("unfold:session-cleared", onSessionCleared);

    return () => {
      cancelledRef.current = true;
      window.removeEventListener("unfold:session-cleared", onSessionCleared);
    };
  }, [syncFromServer]);

  const login = useCallback(() => {
    // In a split-domain deploy (app.unfoldwin.com vs unfoldwin.com), unauthenticated
    // visitors hitting protected app pages are bounced to the marketing site so
    // they can sign up / browse. From anywhere else, route them to the in-app login.
    if (isCrossDomainConfigured() && isOnAppDomain()) {
      window.location.replace(getMarketingUrl());
      return;
    }
    const viteBase = import.meta.env.BASE_URL?.replace(/\/+$/, "") || "";
    const target = getLoginUrl();
    window.location.href = target.startsWith("/") ? `${viteBase}${target}` : target;
  }, []);

  const logout = useCallback(async () => {
    const stored = getStoredSession();
    if (stored) {
      await apiLogout(stored.refreshToken);
    }
    clearSession();
    clearAuthFlag();
    setUser(null);
  }, []);

  const refetch = useCallback(
    (options?: { silent?: boolean }) => {
      void syncFromServer(options?.silent ?? true);
    },
    [syncFromServer],
  );

  // Refetch profile when the tab regains focus (e.g. returning from Stripe checkout).
  useEffect(() => {
    if (typeof document === "undefined") return;
    let lastRefetchedAt = Date.now();
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRefetchedAt < 10_000) return;
      lastRefetchedAt = now;
      void syncFromServer(true);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [syncFromServer]);

  const hydrateUser = useCallback((rawUser: RawAuthUser) => {
    setUser(rawUser as AuthUser);
    setIsLoading(false);
    initialSyncPendingRef.current = false;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetch,
        hydrateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
